import { config } from "../Environment";

export interface TakeawaySource {
  type: "engagement" | "user_created" | "mentor_assigned" | "sketchbook" | "meeting" | "reflection" | "start_funnel" | "capture";
  fiProcess?: string;
  fiPrescription?: string;
  activityTitle?: string;
  engagementName?: string;
  assignedByUser?: {
    _id: string;
    name: string;
    email: string;
  };
  /** Meeting reference — populated when type === "meeting" */
  fiMeeting?: string;
  /** Human-readable meeting title for display */
  meetingTitle?: string;
  /** Daily-reflection context — populated when type === "reflection" */
  question?: string;
  framing?: string;
  dayIndex?: number;
  /** Smart capture — populated when type === "capture" */
  captureId?: string;
  /** Set by the capture flow for hard-moment notes; such items never surface as suggestions. */
  distress?: boolean;
}

export interface UserTakeaway {
  _id: string;
  fiUser: string;
  type: "action" | "insight";
  format: "text" | "html" | "md";
  content: string;
  status: "pending" | "completed" | "archived";
  priority?: "high" | "medium" | "low";
  category?: {
    _id: string;
    name: string;
    color: string;
    type: "action" | "insight" | "both";
  };
  dueDate?: number;
  assignedTo?: {
    _id: string;
    name: string;
    email: string;
  };
  source: TakeawaySource;
  createdOn: number;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  updatedOn: number;
  updatedBy?: {
    _id: string;
    name: string;
    email: string;
  };
  completedOn?: number;

  // Parent-child relationship for sub-tasks
  parentId?: string;
  childIds?: string[];
  subtaskProgress?: {
    total: number;
    completed: number;
  };
  order?: number;

  // Activity linking
  linkedActivity?: {
    prescriptionId: string;
    prescriptionName: string;
    createdOn: number;
    panesCount: number;
    engagementProgress?: {
      started: boolean;
      completedPanes: number;
      totalPanes: number;
      lastEngagementId?: string;
    };
  };

  // Long-term goal flag
  isLongTermGoal?: boolean;

  // Progress reports (enriched by backend)
  progressReportCount?: number;
  latestProgressReport?: {
    content: string;
    createdOn: number;
  } | null;

  // Identity space linking
  spaceIds?: string[];

  // PWF Hybrid Bridge — present only on items sourced from the pwfs collection
  /** True when this row is a pwf-native action_item surfaced by the hybrid
   *  merge (journey steps). Its _id is the pwf id — complete via
   *  togglePwfStatus, defer via updatePwf(config.dueDate). */
  __isPwf?: boolean;
  pwfMeta?: {
    fiParent: string | null;
    ancestorIds: string[];
    children: string[];
    aiContext: any | null;
    /** Back-ref to the pwfs doc this takeaway is rooted to (Experience v3 project link) */
    fiPwf?: string | null;
  } | null;
  manifestations?: Array<{ pwfType: string; isActive: boolean; enabledAt: string }>;
}

export interface CreateTakeawayRequest {
  type: "action" | "insight";
  format?: "text" | "html" | "md";
  content: string;
  priority?: "high" | "medium" | "low";
  category?: string;
  dueDate?: number;
  assignedTo?: string;
  spaceIds?: string[];
  isLongTermGoal?: boolean;
  projectId?: string; // roots the takeaway to a life project (pwfMeta.fiPwf)
  source: {
    type: "engagement" | "user_created" | "mentor_assigned" | "sketchbook" | "meeting" | "reflection";
    fiProcess?: string;
    fiPrescription?: string;
    activityTitle?: string;
    engagementName?: string;
    assignedByUser?: string;
    fiMeeting?: string;
    meetingTitle?: string;
    question?: string;
    framing?: string;
    dayIndex?: number;
  };
}

export interface EditTakeawayRequest {
  id: string;
  type?: "action" | "insight";
  content?: string;
  status?: "pending" | "completed" | "archived";
  priority?: "high" | "medium" | "low" | null;
  category?: string | null;
  dueDate?: number | null;
  assignedTo?: string;
  spaceIds?: string[];
  isLongTermGoal?: boolean;
}

export interface DeleteTakeawayRequest {
  id: string;
}

export interface ToggleStatusRequest {
  id: string;
}

export interface BulkActionRequest {
  ids: string[];
  action: "delete" | "archive" | "complete" | "set_priority" | "set_category";
  value?: string; // Used for set_priority and set_category
}

export interface ListTakeawaysRequest {
  type?: "action" | "insight";
  status?: "pending" | "completed" | "archived";
  priority?: "high" | "medium" | "low";
  category?: string;
  sourceType?: "engagement" | "user_created" | "mentor_assigned" | "sketchbook" | "meeting" | "reflection";
  search?: string;
  assignedTo?: string;
  limit?: number;
  offset?: number;
}

export interface TakeawayResponse {
  status: "CREATED" | "UPDATED" | "DELETED" | "ERROR";
  _id?: string;
  takeaway?: UserTakeaway;
  newStatus?: "pending" | "completed" | "archived";
  fault?: string;
}

export interface ListTakeawaysResponse {
  status: "LIST";
  list: UserTakeaway[];
  total: number;
}

export interface FindTakeawayResponse {
  status: "ITEM" | "ERROR";
  item?: UserTakeaway;
  fault?: string;
}

export interface BulkActionResponse {
  status: "SUCCESS" | "ERROR";
  action?: string;
  count?: number;
  fault?: string;
}

export interface StatsResponse {
  status: "STATS";
  stats: {
    pendingActions: number;
    completedActions: number;
    totalInsights: number;
    pendingTasks: number;
  };
}

/**
 * Create a new takeaway (action item or insight)
 */
export const createTakeaway = async (
  retryFetch: Function,
  data: CreateTakeawayRequest
): Promise<TakeawayResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/user-takeaway/create`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to create takeaway: ${response.statusText}`);
  }

  const result: TakeawayResponse = await response.json();
  return result;
};

/**
 * Edit an existing takeaway
 */
export const editTakeaway = async (
  retryFetch: Function,
  data: EditTakeawayRequest
): Promise<TakeawayResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/user-takeaway/edit`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to edit takeaway: ${response.statusText}`);
  }

  const result: TakeawayResponse = await response.json();
  return result;
};

/**
 * Delete a takeaway
 */
export const deleteTakeaway = async (
  retryFetch: Function,
  data: DeleteTakeawayRequest
): Promise<TakeawayResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/user-takeaway/delete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to delete takeaway: ${response.statusText}`);
  }

  const result: TakeawayResponse = await response.json();
  return result;
};

/**
 * Toggle takeaway status between pending and completed
 */
export const toggleStatus = async (
  retryFetch: Function,
  data: ToggleStatusRequest
): Promise<TakeawayResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/user-takeaway/toggle-status`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to toggle status: ${response.statusText}`);
  }

  const result: TakeawayResponse = await response.json();
  return result;
};

/**
 * Perform bulk action on multiple takeaways
 */
export const bulkAction = async (
  retryFetch: Function,
  data: BulkActionRequest
): Promise<BulkActionResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/user-takeaway/bulk-action`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to perform bulk action: ${response.statusText}`);
  }

  const result: BulkActionResponse = await response.json();
  return result;
};

/**
 * List takeaways with optional filters
 */
export const listTakeaways = async (
  retryFetch: Function,
  filters?: ListTakeawaysRequest
): Promise<ListTakeawaysResponse> => {
  const queryParams = new URLSearchParams();

  if (filters?.type) queryParams.append("type", filters.type);
  if (filters?.status) queryParams.append("status", filters.status);
  if (filters?.priority) queryParams.append("priority", filters.priority);
  if (filters?.category) queryParams.append("category", filters.category);
  if (filters?.sourceType) queryParams.append("sourceType", filters.sourceType);
  if (filters?.search) queryParams.append("search", filters.search);
  if (filters?.assignedTo) queryParams.append("assignedTo", filters.assignedTo);
  if (filters?.limit) queryParams.append("limit", String(filters.limit));
  if (filters?.offset) queryParams.append("offset", String(filters.offset));

  const url = `${config.API_BASE}/api/user-takeaway/list${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  const response = await retryFetch(url, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch takeaways: ${response.statusText}`);
  }

  const result: ListTakeawaysResponse = await response.json();
  return result;
};

/**
 * Find a specific takeaway by ID
 */
export const findTakeaway = async (
  retryFetch: Function,
  id: string
): Promise<FindTakeawayResponse> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/user-takeaway/find?id=${id}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch takeaway: ${response.statusText}`);
  }

  const result: FindTakeawayResponse = await response.json();
  return result;
};

/**
 * Get statistics for the authenticated user's takeaways
 */
export const getStats = async (
  retryFetch: Function
): Promise<StatsResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/user-takeaway/stats`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch stats: ${response.statusText}`);
  }

  const result: StatsResponse = await response.json();
  return result;
};

/**
 * Reorder subtasks under a parent action item
 */
export const reorderSubtasks = async (
  retryFetch: Function,
  data: { parentId: string; orderedIds: string[] }
): Promise<{ status: string; reordered?: number; fault?: string }> => {
  const response = await retryFetch(`${config.API_BASE}/api/user-takeaway/reorder-subtasks`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to reorder subtasks: ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Update the spaceIds array on a takeaway (assign/unassign to spaces)
 */
export const updateTakeawaySpaces = async (
  retryFetch: Function,
  id: string,
  spaceIds: string[]
): Promise<UserTakeaway> => {
  const response = await retryFetch(`${config.API_BASE}/api/user-takeaway/${id}/spaces`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ spaceIds }),
  });

  if (!response.ok) {
    throw new Error(`Failed to update takeaway spaces: ${response.statusText}`);
  }

  return await response.json();
};
